import { ChatModel } from "../types/model";
import { Setting } from "../types/setting";

// Which models this add-in may use, and which one it starts on.
//
// Lifted out of ModelSelect so that a surface with no picker -- filing an email, which
// makes one call and never shows a model -- lands on exactly the model the chat would
// have chosen. Two copies of this would drift the first time either rule changed.

const sameIdentifier = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/**
 * The models the organisation has allowed this add-in to use.
 *
 * Identifiers are stored with inconsistent casing, so every comparison is
 * case-insensitive. An empty selection means "no restriction", not "nothing allowed";
 * and a selection none of whose models is still enabled is treated the same as unset
 * rather than leaving the user unable to do anything at all.
 */
export function availableModels(
  models: ChatModel[],
  availableModelsSetting: Setting | undefined
): ChatModel[] {
  const allowed = availableModelsSetting?.categories;
  if (!allowed || allowed.length === 0) return models;

  const restricted = models.filter((model) =>
    allowed.some((option) => sameIdentifier(option.value, model.objectIdentifier))
  );
  return restricted.length > 0 ? restricted : models;
}

/**
 * The organisation's configured default, falling back to the first available model
 * when it is unset, not among the available models, or could not be read.
 */
export function defaultModel(
  available: ChatModel[],
  defaultModelSetting: Setting | undefined
): ChatModel | undefined {
  const configured = defaultModelSetting?.singleCategoryIdentifier;
  const match = configured
    ? available.find((model) => sameIdentifier(model.objectIdentifier, configured))
    : undefined;
  return match ?? available[0];
}

export function isModelAvailable(available: ChatModel[], model: ChatModel | null): boolean {
  return (
    model !== null &&
    available.some((candidate) =>
      sameIdentifier(candidate.objectIdentifier, model.objectIdentifier)
    )
  );
}
